import { Avatar, Collapse, Spinner, Text, Tag, Icon } from "@chakra-ui/react";
import { Box, Flex } from "@chakra-ui/layout";
import { useState } from "react";
import colors from "core/resources/theme/colors";
import moment from "moment";
import OutlineButton from "ui/components/Button/OutlineButton";
import { FaShare } from "react-icons/fa";
import { Col, Row } from "antd";
import useHistoricoMensagem from "core/features/mensagens/hooks/useHistoricoMensagem";
import { MensagemAnexo } from "core/features/mensagens/typings";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import ResponderMensagem from "./ResponderMensagem";
import { useLocation } from "react-router";
import NumeroLeituras from "ui/pages/Admin/Administracao/Mensagens/NumeroLeituras";
import ModalLeituras from "ui/pages/Admin/Administracao/Mensagens/ModalLeituras";
import { MensagemOrigem } from "core/features/mensagens/typings";
import { AnexoItem } from "ui/components/Anexos";
import useModificarArquivoAnexoMensagem from "core/features/mensagens/hooks/useModificarArquivoAnexoMensagem";
import { checkOrPermission } from "infra/helpers/permission";
import { PermissaoEnum } from "core/resources/enums/permissao";
import { IoIosSwap } from "react-icons/io";
import ModalModificarAnexo from "./ModalModificarAnexo";
import { showSuccess } from "../../../../../../../../infra/helpers/alerta";

interface MensagemProps {
  mensagemId: string;
  showResponder: boolean;
  data: string;
  nome: string;
  conteudo: string;
  anexos: MensagemAnexo[];
  defaultCollapsed?: boolean;
  numRespostas: number;
  defaultOpenRespostas: boolean;
  indent?: number;
  imagemUrl?: string;
  refetchPai?: () => any;
  showLeituras?: boolean;
  numDestinos?: number;
  numDestinosLidos?: number;
  unidade?: string;
  origem?: MensagemOrigem;
  isRefetchingPai?: boolean;
}

function Mensagem({
  mensagemId,
  conteudo,
  unidade,
  data,
  defaultOpenRespostas,
  imagemUrl,
  nome,
  anexos,
  numRespostas,
  showResponder,
  defaultCollapsed,
  indent = 0,
  isRefetchingPai,
  refetchPai,
  showLeituras = false,
  numDestinos,
  numDestinosLidos,
  origem,
}: MensagemProps) {
  const [modalLeiturasOpen, setModalLeiturasOpen] = useState(false);
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const mensagemRespostaId = params.get("mensagemRespostaId") as string;
  const [openResponder, setOpenResponder] = useState(
    mensagemRespostaId === mensagemId
  );
  const [openRespostas, setOpenRespostas] = useState(defaultOpenRespostas);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [anexoToModify, setAnexoToModify] = useState<
    MensagemAnexo | undefined
  >();
  const {
    data: mensagens,
    isLoading,
    isFetching,
    refetch,
  } = useHistoricoMensagem(mensagemId, openRespostas && numRespostas > 0);
  const { mutate: modificarAnexo, isLoading: isLoadingModificarAnexo } =
    useModificarArquivoAnexoMensagem(mensagemId);

  const _isLoading = isLoading || isFetching;

  const _showResponder = showResponder && !openResponder;

  const hasPermissionModificarAnexo = checkOrPermission([
    PermissaoEnum.Backoffice.Administrativo.Mensagem.Criar,
    PermissaoEnum.Backoffice.Administrativo.Portaria.Gerenciar,
  ]);

  return (
    <Box as={Row} gutter={16}>
      <Box
        as={Col}
        xs={"60px"}
        flexDir="column"
        display="flex"
        gap={2}
        alignItems="center"
      >
        <Avatar name={nome} src={imagemUrl} w="40px" h="40px" />
        {isRefetchingPai ? <Spinner /> : null}
      </Box>
      <Flex
        as={Col}
        flexGrow={1}
        w={`calc(100% - 60px - ${indent * 10}px)`}
        xs={`calc(100% - 60px - ${indent * 10}px)`}
        flexDir="column"
      >
        <Flex alignItems="center" mb={4}>
          <Box flexDir={"column"}>
            <Text fontWeight="bold">
              {nome}{" "}
              <Text color={colors.black700} display="inline" fontWeight="400">
                escreveu em {moment(data).format("DD/MM/YYYY")} às{" "}
                {moment(data).format("HH:mm")}
              </Text>
            </Text>

            {unidade ? <Tag>{unidade}</Tag> : null}
          </Box>

          <Flex ml="auto" gridGap={6}>
            {showLeituras && !_isLoading ? (
              <NumeroLeituras
                numDestinos={numDestinos}
                numDestinosLidos={numDestinosLidos}
                onClick={() => setModalLeiturasOpen(true)}
              />
            ) : null}

            {_showResponder ? (
              <OutlineButton
                lineColor={colors.primary}
                size="xs"
                onClick={() => setOpenResponder(true)}
              >
                <Box as="span" transform="scaleX(-1)" mr={2}>
                  <FaShare size={16} />
                </Box>
                Responder
              </OutlineButton>
            ) : null}
          </Flex>
        </Flex>

        {collapsed ? (
          <Flex
            mt={_showResponder ? -2 : 2}
            gridGap={4}
            cursor="pointer"
            onClick={() => setCollapsed(false)}
          >
            <Text color={"blue.500"}>expandir mensagem</Text>
          </Flex>
        ) : null}

        <Collapse in={!collapsed}>
          <Flex flexDir="column">
            <Box
              dangerouslySetInnerHTML={{ __html: conteudo }}
              className="ck-editor"
            />
            {numRespostas > 0 ? (
              <Flex
                mt={2}
                gridGap={2}
                cursor="pointer"
                onClick={() => setOpenRespostas((state) => !state)}
              >
                <Text color={colors.primary}>
                  {numRespostas} {numRespostas === 1 ? "resposta" : "respostas"}
                </Text>
                {openRespostas ? (
                  <FiChevronUp size={24} color={colors.primary} />
                ) : (
                  <FiChevronDown size={24} color={colors.primary} />
                )}
              </Flex>
            ) : null}

            {anexos?.length ? (
              <Box mt="8px" mb="16px">
                {/* {anexos.map((item) => (
                <Tag
                  cursor="pointer"
                  onClick={() => window.open(item.referencia, "_blank")}
                >
                  <MdPhoto />
                  {item.nome}
                </Tag>
              ))} */}

                <Flex gridGap={2}>
                  {anexos.map((anexo, indexFiles) => (
                    <AnexoItem
                      key={indexFiles}
                      url={anexo.referencia}
                      nome={anexo.nome}
                      header={
                        origem === MensagemOrigem.Enviada &&
                        hasPermissionModificarAnexo ? (
                          <Flex
                            justifyContent="flex-end"
                            alignItems="center"
                            h="100%"
                            w="100%"
                          >
                            <Icon
                              as={IoIosSwap}
                              w={6}
                              h={6}
                              color={colors.primary}
                              mr={2}
                              onClick={() => setAnexoToModify(anexo)}
                              cursor="pointer"
                              role="button"
                              title="Modificar arquivo"
                            />
                          </Flex>
                        ) : null
                      }
                    />
                  ))}
                </Flex>
              </Box>
            ) : null}

            <Collapse in={openRespostas}>
              <Flex flexDir="column" w="100%">
                {mensagens?.items.map((mensagem, index) => (
                  <>
                    <Box mt={4} key={mensagem.id}>
                      <Mensagem
                        mensagemId={mensagem.id}
                        showResponder={
                          origem === MensagemOrigem.Recebida ||
                          origem === MensagemOrigem.Excluida
                            ? false
                            : indent < 1
                        }
                        data={mensagem.createdAt}
                        nome={mensagem.autor}
                        anexos={mensagem.anexos}
                        conteudo={mensagem.conteudo}
                        imagemUrl={mensagem.imagemUrl}
                        numRespostas={mensagem.numRespostas}
                        defaultOpenRespostas={false}
                        defaultCollapsed={index !== mensagens?.items.length - 1}
                        indent={indent + 1}
                        refetchPai={refetch}
                        isRefetchingPai={isFetching}
                      />
                    </Box>
                  </>
                ))}

                {_isLoading ? (
                  <Spinner
                    color={colors.primary}
                    alignSelf="center"
                    mt={4}
                    size="md"
                  />
                ) : null}
              </Flex>
            </Collapse>
          </Flex>
        </Collapse>
        {openResponder ? (
          <ResponderMensagem
            mensagemRespostaId={mensagemId}
            nomeDestino={nome}
            onClose={() => setOpenResponder(false)}
            onSuccess={() => {
              setOpenResponder(false);
              refetchPai?.();
              if (indent === 0) {
                refetch?.();
              }
            }}
          />
        ) : null}

        {showLeituras ? (
          <ModalLeituras
            isOpen={modalLeiturasOpen}
            onClose={() => setModalLeiturasOpen(false)}
            mensagemId={mensagemId}
            numDestinos={numDestinos}
            numDestinosLidos={numDestinosLidos}
          />
        ) : null}

        {anexoToModify ? (
          <ModalModificarAnexo
            isOpen={!!anexoToModify}
            onClose={() => setAnexoToModify(undefined)}
            isLoadingSave={isLoadingModificarAnexo}
            mimetype={anexoToModify.mimetype}
            onSubmit={(file) => {
              modificarAnexo(
                {
                  arquivoId: anexoToModify.arquivoId,
                  file,
                },
                {
                  onSuccess: () => {
                    showSuccess().then(() => {
                      refetchPai?.();
                      setAnexoToModify(undefined);
                    });
                  },
                }
              );
            }}
          />
        ) : null}
      </Flex>
    </Box>
  );
}

export default Mensagem;
